import {
    AgeRestriction,
    ApkInfoWithTargets,
    BackendType,
    InstallableAppScreenshot,
    LanguageCode,
    LicenseAgreement,
    RegionCode,
} from '../types';

import { Example, SmartHomeDeepLinks } from './common';
import { Image } from './image';
import { Channel } from './skill';
import { UserAgreementOrDraft } from './userAgreement';

interface GrammarsObj {
    grammar: string;
}

export enum SkillAccess {
    Public = 'public',
    Hidden = 'hidden',
    Private = 'private',
}

export interface CommonSettings<BS extends BackendSettings, PS extends PublishingSettings> {
    name: string;
    activationPhrases: string[];
    backendSettings: BS;
    publishingSettings: PS;
    yaCloudGrant: boolean;
    logoId: string | null;
    logoUrl: string | null;
    logo2: Image | null;
    hideInStore: boolean;
    noteForModerator: string;
    requiredInterfaces: string[];
    exactSurfaces: string[];
    surfaceWhitelist: string[];
    surfaceBlacklist: string[];
    voice: string;
    oauthAppId: string;
    grammars: GrammarsObj;
    appMetricaApiKey: string;
    useStateStorage: boolean;
    rsyPlatformId: string;
    isTrustedSmartHomeSkill: boolean;
    customEntities: string;
    skillAccess: SkillAccess;
    enableAllAvailableRegions: boolean;
    selectedRegions: RegionCode[];
    skillUserAgreements?: UserAgreementOrDraft[];
}

export type BackendSettings = object;

export interface AliceSkillBackendSettings extends BackendSettings {
    uri?: string;
    functionId?: string;
    backendType?: BackendType;
    isVoiceControlDisabled?: boolean;
    isAdvancedVoiceControlSupported?: boolean;
}

export interface SmartHomeBackendSettings extends BackendSettings {
    uri: string;
    functionId: string;
    backendType: BackendType;
}

export interface MultilingualSetupEntry {
    description?: string;

    externalSettingsDescription: string;
    supportedUnitsDescription?: string;
    supportServiceUrl?: string;

    name: string;
    secondaryTitle?: string;
}

export interface PublishingSettings {
    brandIsVerified?: boolean;
    brandVerificationWebsite?: string;
    category?: string;
    developerName?: string;
    email?: string;
    emailIsVerified?: boolean;
    secondaryTitle?: string;
}

export interface AliceSkillPublishingSettings extends PublishingSettings {
    description?: string;
    explicitContent?: boolean;
    structuredExamples?: Example[];
}

export interface SmartHomePublishingSettings extends PublishingSettings {
    description?: string;
    smartHome?: {
        isTrusted?: boolean;
        deepLinks?: SmartHomeDeepLinks;
    };
    multilingualSettings?: { [key in LanguageCode]?: MultilingualSetupEntry };
}

export interface InstallableAppPublishingSettings extends PublishingSettings {
    description?: string;
    hasMonetization?: boolean;
}

export const LogoImage = {
    square: 'square',
    backgroundImage: 'backgroundImage',
} as const;

export type TLogoImage = (typeof LogoImage)[keyof typeof LogoImage];

export interface LogoDto {
    id?: string;
    imageId: string;
    imageType: TLogoImage;
    url?: string;
}

export type AliceSkillSettings = CommonSettings<AliceSkillBackendSettings, AliceSkillPublishingSettings>;

export type SmartHomeSettings = CommonSettings<SmartHomeBackendSettings, SmartHomePublishingSettings>;

export type InstallableAppSettings = {
    // refactor: rethink common settings and inherit
    activationPhrases: string[];
    backendSettings: Partial<AliceSkillBackendSettings>;
    channel: Channel;
    packageName: string;
    noteForModerator: string;
    name: string;
    firstPublishedAt?: string;
    publishingSettings: InstallableAppPublishingSettings;
    ageRestriction?: AgeRestriction;
    logoId: string | null;
    logoUrl: string;
    secondaryLogoId: string | null; // PATCH
    secondaryLogoUrl: string | null; // PATCH
    secondaryLogo?: {
        // GET
        imageId: string;
        imageUrl: string;
    };
    logos?: LogoDto[]; // GET
    additionalLogos?: LogoDto[]; // PATCH
    apkInfosWithTargets: ApkInfoWithTargets[];
    multiApkEnabled?: boolean;
    isTrusted: boolean;
    screenshots?: InstallableAppScreenshot[];
    privacyAgreement?: LicenseAgreement;
    eulaAgreement?: LicenseAgreement;
    secondaryTitle?: string;
    skillAccess: SkillAccess;
    voiceControlEnable: boolean;
    isAdvancedVoiceControlSupported: boolean;
};

export type Settings = AliceSkillSettings | SmartHomeSettings;
